import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt } from 'react-icons/fa';
import Datepicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import axios from 'axios';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const Inventory = () => {
  const navigate = useNavigate();
  const [selectedDates, setSelectedDates] = useState([null, null]);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const calendarRef = useRef(null);
  const buttonRef = useRef(null);
  const [roomDetails, setRoomDetails] = useState(null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [occupancyData, setOccupancyData] = useState({
    average_occupancy_rate: 0,
    total_booked_days: 0,
    total_possible_room_days: 0,
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRoomDetails = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/roomdetails`);
        if (!response.ok) {
          throw new Error('Failed to fetch room details.');
        }
        const data = await response.json();
        setRoomDetails(data);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchRoomDetails();
  }, []);

  useEffect(() => {
    const fetchRoomInfo = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/rooms/search`);
        const data = await response.json();
        setRoomInfo(data.rooms);
      } catch (error) {
        console.error('Error fetching room details:', error);
      }
    };

    fetchRoomInfo();
  }, []);

  // Fetch occupancy data when selected dates change
  useEffect(() => {
    if (selectedDates[0]) {
      const fetchOccupancyData = async () => {
        try {
          let response;
          let occupancyData = {};
          if (selectedDates[1]) {
            // Date Range selected
            response = await axios.get(`${BQ_BASE_URL}/bq/api/occupancy/average_occupancy_rate`, {
              params: {
                start_date: format(selectedDates[0], 'yyyy-MM-dd'),
                end_date: format(selectedDates[1], 'yyyy-MM-dd'),
              },
            });
            occupancyData = {
              average_occupancy_rate: response.data.average_occupancy_rate,
              total_booked_days: response.data.total_booked_days,
              total_possible_room_days: response.data.total_possible_room_days,
            };
          } else {
            // Single Date selected
            response = await axios.get(`${BQ_BASE_URL}/bq/api/occupancy/occupancy_rate_for_date`, {
              params: {
                date: format(selectedDates[0], 'yyyy-MM-dd'),
              },
            });
            occupancyData = {
              average_occupancy_rate: response.data.occupancy_rate,
              total_booked_days: response.data.total_booked_rooms,
              total_possible_room_days: response.data.total_rooms,
            };
          }
          setOccupancyData(occupancyData);
        } catch (error) {
          console.error('Error fetching occupancy data:', error);
        }
      };

      fetchOccupancyData();
    }
  }, [selectedDates]);

  useEffect(() => {
    const fetchTodayOccupancy = async () => {
      try {
        const today = format(new Date(), 'yyyy-MM-dd');
        const response = await axios.get(
          `${BQ_BASE_URL}/bq/api/occupancy/occupancy_rate_for_date`,
          {
            params: { date: today },
          }
        );
        setOccupancyData({
          average_occupancy_rate: response.data.occupancy_rate,
          total_booked_days: response.data.total_booked_rooms,
          total_possible_room_days: response.data.total_rooms,
        });
      } catch (error) {
        console.error("Error fetching today's occupancy data:", error);
      }
    };

    fetchTodayOccupancy();
  }, []);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarVisible &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setCalendarVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [calendarVisible]);

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setSelectedDates([start, end]);

    // Close calendar when both dates are selected in range mode
    if (start && end) {
      setCalendarVisible(false);
    }
  };

  const handleCalendarToggle = () => {
    setCalendarVisible(!calendarVisible);
  };

  return (
    <div className="bg-gray-100 p-6 relative min-h-screen">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold mb-2 md:mb-0 md:ml-2">Room Inventory</h1>
        <div className="flex flex-wrap gap-2 md:space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/room-management/overview')}
            className="btn text-black font-bold"
          >
            Overview
          </button>
          <button onClick={() => navigate('/dashboard/mdm/room-management/rooms')} className="btn">
            Room Management
          </button>
          {/* <button
            onClick={() =>
              navigate("/dashboard/mdm/room-management/order-summary")
            }
            className="btn"
          >
            Order Summary
          </button> */}
        </div>
      </header>

      {/* Selected Dates and Calendar */}
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-end relative">
        <div className="flex items-center space-x-2 bg-white p-2 mb-2 sm:mb-0">
          <span className="font-semibold text-black">Select Dates:</span>
          <span className="border border-gray-300 px-3 py-1 inline-block">
            <span className="text-gray-900 font-semibold">
              {selectedDates[0]
                ? `${format(selectedDates[0], 'dd MMM, yyyy')} ${
                    selectedDates[1] ? `- ${format(selectedDates[1], 'dd MMM, yyyy')}` : ''
                  }`
                : format(new Date(), 'EEE MMM dd yyyy')}
            </span>
          </span>
        </div>
        <button
          ref={buttonRef}
          onClick={handleCalendarToggle}
          className="btn flex items-center bg-white p-3.5"
        >
          <FaCalendarAlt className="text-xl" />
        </button>

        {calendarVisible && (
          <div
            ref={calendarRef}
            className="absolute z-50 p-4 bg-white shadow-lg rounded-md mt-1 right-0 top-full"
            style={{
              width: 'auto',
              maxWidth: '95vw',
            }}
          >
            <h3 className="text-base font-semibold text-gray-700 mb-2">Select Dates</h3>
            <Datepicker
              selected={selectedDates[0]}
              onChange={handleDateChange}
              startDate={selectedDates[0]}
              endDate={selectedDates[1]}
              selectsRange
              shouldCloseOnSelect={false}
              inline
            />
          </div>
        )}
      </div>

      {/* Key Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-4">
        <div className="bg-white p-3 rounded-lg shadow-md">
          <h3 className="font-semibold text-sm text-gray-700">Total Rooms</h3>
          <p className="text-3xl font-bold text-gray-800">{roomDetails?.total_rooms || 0}</p>
        </div>
        {roomDetails?.room_status_counts
          ? Object.keys(roomDetails.room_status_counts).map((type) => {
              const counts = roomDetails.room_status_counts[type] || 0;
              return (
                <div className="bg-white p-3 rounded-lg shadow-md" key={type}>
                  <h3 className="font-semibold text-sm whitespace-nowrap text-gray-700">{type}</h3>
                  <p className="text-3xl font-bold text-gray-800">{counts}</p>
                </div>
              );
            })
          : null}

        {/* New Occupancy Data Cards */}
        <div className="bg-white p-3 rounded-lg shadow-md">
          <h3 className="font-semibold text-sm whitespace-nowrap text-gray-700">
            Average Occupancy
          </h3>
          <p className="text-3xl font-bold text-gray-800">
            {occupancyData.average_occupancy_rate}%
          </p>
        </div>
      </div>

      {/* Room Status Overview */}
      <div className="mb-2">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Room Status Overview</h2>
        <div className="overflow-x-auto bg-white rounded-lg shadow-md">
          {roomInfo && Object.keys(roomInfo).length > 0 ? (
            <table className="min-w-full table-auto border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="py-3 px-4 text-left text-gray-700 font-semibold">Room Type</th>
                  <th className="py-3 px-4 text-center text-gray-700 font-semibold">
                    Available Rooms
                  </th>
                  <th className="py-3 px-4 text-center text-gray-700 font-semibold">
                    Reserved Rooms
                  </th>
                  <th className="py-3 px-4 text-center text-gray-700 font-semibold">
                    Occupied Rooms
                  </th>
                  <th className="py-3 px-4 text-center text-gray-700 font-semibold">
                    Under Maintenance
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(roomInfo).map((roomType) => {
                  const statusCounts = roomInfo[roomType];
                  return (
                    <tr key={roomType} className="hover:bg-gray-50 border-b">
                      <td className="py-3 px-4 text-gray-700">{roomType}</td>
                      <td className="py-3 px-4 text-center text-green-600 font-medium">
                        {statusCounts.Available ? statusCounts.Available.count : 0}
                      </td>
                      <td className="py-3 px-4 text-center text-red-600 font-medium">
                        {statusCounts.Reserved ? statusCounts.Reserved.count : 0}
                      </td>
                      <td className="py-3 px-4 text-center text-cyan-600 font-medium">
                        {statusCounts.Occupied ? statusCounts.Occupied.count : 0}
                      </td>
                      <td className="py-3 px-4 text-center text-cyan-600 font-medium">
                        {statusCounts['Under maintenance']
                          ? statusCounts['Under maintenance'].count
                          : 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-600 text-center p-4">Room details loading...</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inventory;

// import React, { useState, useEffect, useRef } from "react";
// import { useNavigate } from "react-router-dom";
// import { FaCalendarAlt } from "react-icons/fa";
// import Datepicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import { format } from "date-fns";
// import axios from "axios";
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const Inventory = () => {
//   const navigate = useNavigate();
//   const [selectedDates, setSelectedDates] = useState([null, null]); // To handle both date range or single date
//   const [calendarVisible, setCalendarVisible] = useState(false);
//   const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
//   const buttonRef = useRef(null);
//   const [roomDetails, setRoomDetails] = useState(null);
//   const [roomInfo, setRoomInfo] = useState(null);
//   const [occupancyData, setOccupancyData] = useState({
//     average_occupancy_rate: 0,
//     total_booked_days: 0,
//     total_possible_room_days: 0,
//   });
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchRoomDetails = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/roomdetails`);
//         if (!response.ok) {
//           throw new Error("Failed to fetch room details.");
//         }
//         const data = await response.json();
//         setRoomDetails(data);
//       } catch (err) {
//         setError(err.message);
//       }
//     };

//     fetchRoomDetails();
//   }, []);

//   useEffect(() => {
//     const fetchRoomInfo = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/rooms/search`);
//         const data = await response.json();
//         setRoomInfo(data.rooms);
//       } catch (error) {
//         console.error("Error fetching room details:", error);
//       }
//     };

//     fetchRoomInfo();
//   }, []);

//   // Fetch occupancy data when selected dates change (both single date and range)
//   useEffect(() => {
//     if (selectedDates[0]) {
//       const fetchOccupancyData = async () => {
//         try {
//           let response;
//           let occupancyData = {};
//           if (selectedDates[1]) {
//             // Date Range selected, use the range API
//             response = await axios.get(
//               `${BQ_BASE_URL}/bq/api/occupancy/average_occupancy_rate`,
//               {
//                 params: {
//                   start_date: format(selectedDates[0], "yyyy-MM-dd"),
//                   end_date: format(selectedDates[1], "yyyy-MM-dd"),
//                 },
//               }
//             );
//             occupancyData = {
//               average_occupancy_rate: response.data.average_occupancy_rate,
//               total_booked_days: response.data.total_booked_days,
//               total_possible_room_days: response.data.total_possible_room_days,
//             };
//           } else {
//             // Single Date selected, use the single date API
//             response = await axios.get(
//               `${BQ_BASE_URL}/bq/api/occupancy/occupancy_rate_for_date`,
//               {
//                 params: {
//                   date: format(selectedDates[0], "yyyy-MM-dd"),
//                 },
//               }
//             );
//             occupancyData = {
//               average_occupancy_rate: response.data.occupancy_rate,
//               total_booked_days: response.data.total_booked_rooms,
//               total_possible_room_days: response.data.total_rooms,
//             };
//           }
//           setOccupancyData(occupancyData);
//         } catch (error) {
//           console.error("Error fetching occupancy data:", error);
//         }
//       };

//       fetchOccupancyData();
//     }
//   }, [selectedDates]);

//   useEffect(() => {
//     const fetchTodayOccupancy = async () => {
//       try {
//         const today = format(new Date(), "yyyy-MM-dd"); // Format today's date
//         const response = await axios.get(
//           `${BQ_BASE_URL}/bq/api/occupancy/occupancy_rate_for_date`,
//           {
//             params: { date: today },
//           }
//         );
//         // Normalize the API response into the existing state structure
//         setOccupancyData({
//           average_occupancy_rate: response.data.occupancy_rate,
//           total_booked_days: response.data.total_booked_rooms,
//           total_possible_room_days: response.data.total_rooms,
//         });
//       } catch (error) {
//         console.error("Error fetching today's occupancy data:", error);
//       }
//     };

//     fetchTodayOccupancy();
//   }, []);

//   const handleDateChange = (dates) => {
//     const [start, end] = dates;

//     if (end) {
//       // If both dates are selected, set the range
//       setSelectedDates([start, end]);
//     } else {
//       // If only one date is selected, keep the second date null
//       setSelectedDates([start, null]);
//     }
//   };

//   const handleCalendarToggle = () => {
//     const buttonRect = buttonRef.current.getBoundingClientRect();
//     const top = buttonRect.bottom + window.scrollY - 62;
//     const right = buttonRect.right + window.scrollX - 1340;
//     setCalendarPosition({ top, right });
//     setCalendarVisible(!calendarVisible);
//   };

//   return (
//     <div className="bg-gray-100 p-6 relative h-full">
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">Room Inventory</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate("/dashboard/mdm/room-management/overview")}
//             className="btn text-black font-bold"
//           >
//             Overview
//           </button>
//           <button
//             onClick={() => navigate("/dashboard/mdm/room-management/rooms")}
//             className="btn"
//           >
//             Room Management
//           </button>
//           <button
//             onClick={() =>
//               navigate("/dashboard/mdm/room-management/order-summary")
//             }
//             className="btn"
//           >
//             Order Summary
//           </button>
//         </div>
//       </header>

//       {/* Selected Dates and Calendar */}
//       <div className="mb-6 flex items-center justify-end ">
//         <div className="flex items-center space-x-2 bg-white  p-2">
//           <span className="font-semibold  text-black">Select Dates:</span>
//           <span className="border border-gray-300 px-3 py-1 inline-block ">
//             <span className="text-gray-900 font-semibold">
//               {selectedDates[0]
//                 ? `${format(selectedDates[0], "dd MMM, yyyy")} ${
//                     selectedDates[1]
//                       ? `- ${format(selectedDates[1], "dd MMM, yyyy")}`
//                       : ""
//                   }`
//                 : format(new Date(), "EEE MMM dd yyyy")}
//             </span>
//           </span>
//         </div>
//         <button
//           ref={buttonRef}
//           onClick={handleCalendarToggle}
//           className="btn flex items-center bg-white p-3.5"
//         >
//           <FaCalendarAlt className="text-xl" />
//         </button>
//       </div>

//       {calendarVisible && (
//         <div
//           className="absolute z-50 p-4 bg-white shadow-lg rounded-md"
//           style={{
//             top: calendarPosition.top,
//             right: calendarPosition.right,
//             width: "auto",
//           }}
//         >
//           <h3 className="text-base font-semibold text-gray-700 ">
//             Select Dates
//           </h3>
//           <Datepicker
//             selected={selectedDates[0]}
//             onChange={handleDateChange}
//             startDate={selectedDates[0]}
//             endDate={selectedDates[1]}
//             selectsRange
//             shouldCloseOnSelect={true}
//             inline
//           />
//         </div>
//       )}

//       {/* Key Statistics */}
//       <div className="grid grid-cols-6 gap-4 mb-4">
//         <div className="bg-white p-3 rounded-lg shadow-md">
//           <h3 className="font-semibold text-sm text-gray-700">Total Rooms</h3>
//           <p className="text-3xl font-bold text-gray-800">
//             {roomDetails?.total_rooms || 0}
//           </p>
//         </div>
//         {roomDetails?.room_status_counts
//           ? Object.keys(roomDetails.room_status_counts).map((type) => {
//               const counts = roomDetails.room_status_counts[type] || 0;
//               return (
//                 <div className="bg-white p-3 rounded-lg shadow-md" key={type}>
//                   <h3 className="font-semibold text-sm whitespace-nowrap text-gray-700">
//                     {type}
//                   </h3>
//                   <p className="text-3xl font-bold text-gray-800">{counts}</p>
//                 </div>
//               );
//             })
//           : null}

//         {/* New Occupancy Data Cards */}
//         <div className="bg-white p-3 rounded-lg shadow-md">
//           <h3 className="font-semibold text-sm whitespace-nowrap text-gray-700">
//             Average Occupancy
//           </h3>
//           <p className="text-3xl font-bold text-gray-800">
//             {occupancyData.average_occupancy_rate}%
//           </p>
//         </div>
//       </div>

//       {/* Room Status Overview */}
//       <div className="mb-2">
//         <h2 className="text-xl font-semibold text-gray-800 mb-4">
//           Room Status Overview
//         </h2>
//         <div className="overflow-x-auto bg-white rounded-lg shadow-md">
//           {roomInfo && Object.keys(roomInfo).length > 0 ? (
//             <table className="min-w-full table-auto border-collapse">
//               <thead>
//                 <tr className="bg-gray-200">
//                   <th className="py-3 px-4 text-left text-gray-700 font-semibold">
//                     Room Type
//                   </th>
//                   <th className="py-3 px-4 text-center text-gray-700 font-semibold">
//                     Available Rooms
//                   </th>
//                   <th className="py-3 px-4 text-center text-gray-700 font-semibold">
//                     Reserved Rooms
//                   </th>
//                   <th className="py-3 px-4 text-center text-gray-700 font-semibold">
//                     Occupied Rooms
//                   </th>
//                   <th className="py-3 px-4 text-center text-gray-700 font-semibold">
//                     Unavailable
//                   </th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {Object.keys(roomInfo).map((roomType) => {
//                   const statusCounts = roomInfo[roomType];
//                   return (
//                     <tr key={roomType} className="hover:bg-gray-50 border-b">
//                       <td className="py-3 px-4 text-gray-700">{roomType}</td>
//                       <td className="py-3 px-4 text-center text-green-600 font-medium">
//                         {statusCounts.Available
//                           ? statusCounts.Available.count
//                           : 0}
//                       </td>
//                       <td className="py-3 px-4 text-center text-red-600 font-medium">
//                         {statusCounts.Reserved
//                           ? statusCounts.Reserved.count
//                           : 0}
//                       </td>
//                       <td className="py-3 px-4 text-center text-cyan-600 font-medium">
//                         {statusCounts.Occupied
//                           ? statusCounts.Occupied.count
//                           : 0}
//                       </td>
//                       <td className="py-3 px-4 text-center text-cyan-600 font-medium">
//                         {statusCounts.Unavailable
//                           ? statusCounts.Unavailable.count
//                           : 0}
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           ) : (
//             <p className="text-gray-600 text-center p-4">
//               Room details loading...
//             </p>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Inventory;
